import { FeatureFlagIds } from "../../../utils/featureFlagIds";
import CardHeader from "@mui/material/CardHeader";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import EditIcon from "@mui/icons-material/Edit";
import Divider from "@mui/material/Divider";
import CardContent from "@mui/material/CardContent";
import { hasValue } from "@amsanalytics/react-shared-library";
import Typography from "@mui/material/Typography";
import { CardRawDataViewer } from "../DealerServiceHistoryListView/DealerServiceHistoryListView";
import Card from "@mui/material/Card";
import React, { useEffect } from "react";
import { IConfiguration } from "../../../Data/ConfigurationData";
import ErrorDialog from "../../Dialogs/ErrorDialog";
import {
  getApplicationScore,
  getNewApplicationScore,
  getPrimary700CreditTransactionId
} from "../../../store/application/actions";
import { useDispatch, useSelector } from "react-redux";
import { AppState } from '../../../store/rootReducer';
import { IApplication } from "../../../Interfaces/Application/IApplication";
import { useDialog } from "../../../providers/DialogProvider";
import { IApplicationScore } from "../../../Interfaces/Application/IApplicationScore";
import { IQuote } from "../../../Interfaces/Common/IQuote";
import { IAppCustomer } from "../../../Interfaces/Application/IAppCustomer";
import { ApplicationDispatch } from "../../../store/application/types";
import { TabName } from "./OrderEditorTabs";
import { useBusinessRulesEngine } from "../../../Data/BusinessRulesData";
import { SummaryValueRow, SummaryValueRowProps } from "../../ApplicationSummaryCard/ApplicationSummaryCard";
import { Tooltip } from "@mui/material";

interface CustomFieldSummaryValueRowProps extends SummaryValueRowProps {
  useLargeText?: boolean;
}

// Shows the title and value of a field in the application summary card.
function CustomFieldSummaryValueRow(props: CustomFieldSummaryValueRowProps) {
  return (
    <div style={props.useLargeText ? { display: "flex", flexDirection: "row", justifyContent: "space-between", borderBottom: 'solid 1px #e0e0e0', fontSize: '1.1em' } : { display: "flex", flexDirection: "row", justifyContent: "space-between", borderBottom: 'solid 1px #e0e0e0' }}>
      <div className={'titleText'} style={props.useLargeText ? { flexShrink: 0, fontSize: '1.1em' } : { flexShrink: 0 }}>{props.title + ':'}</div>
      {props.value && (
        <Tooltip title={props.value?.toString() ?? ''}>
          <div style={{ overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis', minWidth: 0 }}>{props.value}</div>
        </Tooltip>
      )}
      {!props.value && <div>{props.value}</div>}
    </div>
  );
}

export interface CreditQualityAndRiskMetricsCardProps {
  currentApp: IApplication;
  configuration: IConfiguration;
  underwriterQuote: IQuote | undefined;
  cobuyer1: IAppCustomer | undefined;
  dispatch: ApplicationDispatch;
  navigate: (tabName: TabName) => void;
}

export const CreditQualityAndRiskMetricsCard = (props: CreditQualityAndRiskMetricsCardProps) => {
  const [openDialog, closeDialog] = useDialog();
  const primaryTransactionId = useSelector((state: AppState) => state.applications.creditPullPrimaryTransactionId);
  const stabilityScore = props.currentApp.applicationCustomFields?.find(x => x.customField?.name === 'Stability Score');
  const customerScore = props.currentApp.applicationCustomFields?.find(x => x.customField?.name === 'Customer Score');
  const dealStructureScore = props.currentApp.applicationCustomFields?.find(x => x.customField?.name === 'Deal Structure Score');
  const totalScore = props.currentApp.applicationCustomFields?.find(x => x.customField?.name === 'Total Score');
  const applicationScore: IApplicationScore | undefined = useSelector((state: AppState) => state.applications.applicationScore);
  const businessRulesEngine = useBusinessRulesEngine();
  const dispatch = useDispatch();

  // Returns the credit tier for this application.
  const getCreditTier = () => {
    return props.currentApp.applicationCustomFields?.find(x => x.customField?.name?.toLowerCase() === 'tier')?.value ?? '';
  }

  const getNewerApplicationScore = () => {
    // If the record hasn't had credit pulled successfully, it will alert them
    if (!hasValue(primaryTransactionId)) {
      openDialog({
        fullScreen: false,
        children: (
          <ErrorDialog
            title='Scoring Error'
            errors={['Credit must be pulled before retrieving the application score']}
            closeDialog={closeDialog}
          />
        ),
      });
      return;
    }

    if (props.currentApp && hasValue(primaryTransactionId)) {
      props.dispatch(getNewApplicationScore(props.currentApp.id));
    }
  }

  const usesApplicationScoring = props.configuration?.featureFlags[FeatureFlagIds.Scienaptic] === true || props.configuration?.featureFlags[FeatureFlagIds.CBD] === true;
  const scoringSource = props.configuration?.featureFlags[FeatureFlagIds.Scienaptic] === true
    ? "Scienaptic"
    : props.configuration?.featureFlags[FeatureFlagIds.CBD] === true
      ? "CBD"
      : "";

  useEffect(() => {
    if (usesApplicationScoring) {
      dispatch(getPrimary700CreditTransactionId(props.currentApp.id));
      dispatch(getApplicationScore(props.currentApp.id, businessRulesEngine));
    }
  }, [props.currentApp, usesApplicationScoring]);

  return (
    <Card>
      <CardHeader
        avatar={<TrendingUpIcon fontSize="small" />}
        title={"Credit Quality and Risk Metrics"}
        action={
          <>
            {usesApplicationScoring &&
              <Button
                style={{ marginRight: "10px" }}
                color="inherit"
                variant="contained"
                onClick={getNewerApplicationScore}
              >
                {`${scoringSource} Score`}
              </Button>
            }
            <IconButton size="small" onClick={() => props.navigate("Applicant")}>
              <EditIcon fontSize="small" />
            </IconButton>
          </>
        }
      />
      <Divider variant="middle" />
      <CardContent>
        {props.underwriterQuote?.scoreJson
          ? Object.keys(JSON.parse(props.underwriterQuote?.scoreJson))
            .filter(x => x !== 'warning')
            .map((key: string) =>
              <SummaryValueRow
                {...props}
                key={key}
                title={key}
                value={JSON.parse(props.underwriterQuote?.scoreJson || '')?.[key] || ''}
              />
            ) : (
            <div>
              {props.configuration.featureFlags[FeatureFlagIds.UseInternalAmsScore] &&
                <SummaryValueRow
                  {...props}
                  title={"Internal Score"}
                  value={props.underwriterQuote?.amsscore || ''}
                />
              }
              {hasValue(stabilityScore?.value) &&
                <SummaryValueRow
                  {...props}
                  title={"Stability Score"}
                  value={stabilityScore!.value}
                />
              }
              {hasValue(customerScore?.value) &&
                <SummaryValueRow
                  {...props}
                  title={"Customer Score"}
                  value={customerScore!.value}
                />
              }
              {hasValue(dealStructureScore?.value) &&
                <SummaryValueRow
                  {...props}
                  title={"Deal Structure Score"}
                  value={dealStructureScore!.value}
                />
              }
              {hasValue(totalScore?.value) &&
                <SummaryValueRow
                  {...props}
                  title={"Total Score"}
                  value={totalScore!.value}
                />
              }
            </div>
          )}
        {hasValue(getCreditTier()) && (
          <SummaryValueRow
            {...props}
            title={"Tier"}
            value={getCreditTier()}
          />
        )}
        {usesApplicationScoring &&
          <div>
            <Typography style={{ marginBottom: 5 }}>
              {`${scoringSource} Score`}
            </Typography>
            <SummaryValueRow
              {...props}
              title={"Decision"}
              value={applicationScore?.decision ?? ''}
            />
            <SummaryValueRow
              {...props}
              title={"Final Grade"}
              value={applicationScore?.finalGrade ?? ''}
            />
            <SummaryValueRow
              {...props}
              title={"Applicant Score"}
              value={applicationScore?.applicantScore ?? ''}
            />
            <SummaryValueRow
              {...props}
              title={"Applicant Grade"}
              value={applicationScore?.applicantGrade ?? ''}
            />
            {applicationScore?.applicantReviewAlerts?.split("|").map((aa, key) =>
              <SummaryValueRow
                {...props}
                key={key}
                title={`Applicant Alert ${key + 1}`}
                value={aa ?? ''}
              />
            ) ?? <div></div>}
            {props.cobuyer1 &&
              <div>
                <SummaryValueRow
                  {...props}
                  title={"Co-Applicant Score"}
                  value={applicationScore?.coApplicantScore ?? ''}
                />
                <SummaryValueRow
                  {...props}
                  title={"Co-Applicant Grade"}
                  value={applicationScore?.coApplicantGrade ?? ''}
                />
                {applicationScore?.coApplicantReviewAlerts?.split("|").map((ca, key) =>
                  <SummaryValueRow
                    {...props}
                    key={key}
                    title={`Co-Applicant Alert ${key + 1}`}
                    value={ca ?? ''}
                  />
                ) ?? <div></div>}
              </div>
            }
            {applicationScore?.positiveContributors?.split("|").map((pc, key) =>
              <CustomFieldSummaryValueRow
                {...props}
                key={key}
                title={`Positive Contributor ${key + 1}`}
                value={pc ?? ''}
              />
            )}
            {applicationScore?.negativeContributors?.split("|").map((nc, key) =>
              <CustomFieldSummaryValueRow
                {...props}
                key={key}
                title={`Negative Contributor ${key + 1}`}
                value={nc ?? ''}
              />
            )}
            {hasValue(applicationScore?.rawData) &&
              <CardRawDataViewer rawData={applicationScore?.rawData ?? ""} type={"JSON"} />
            }
          </div>
        }
      </CardContent>
    </Card>
  );
}